import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiBarChart2, FiUsers, FiCalendar, FiTrendingUp,
  FiAlertTriangle, FiFilter
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { reuniaoCriancasService } from '../../services/api';
import './ReuniaoCriancas.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const calcularIdade = (d) => d ? Math.floor((new Date() - new Date(d)) / 31557600000) : null;

const ReuniaoCriancasDashboard = () => {
  const navigate = useNavigate();
  const chartRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('6m');
  const [congregacao, setCongregacao] = useState('');

  const carregarDados = async () => {
    try {
      setLoading(true);
      const params = {};
      if (congregacao) params.congregacao = congregacao;
      const hoje = new Date();
      if (periodo !== 'todos') {
        const meses = parseInt(periodo) || 6;
        const inicio = new Date(hoje);
        inicio.setMonth(inicio.getMonth() - meses);
        params.dataInicio = inicio.toISOString().split('T')[0];
        params.dataFim = hoje.toISOString().split('T')[0];
      }
      const resp = await reuniaoCriancasService.dashboard(params);
      setData(resp);
    } catch {
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregarDados(); }, [periodo, congregacao]); // eslint-disable-line

  const chartData = useMemo(() => {
    if (!data?.serieTemporal?.length) return null;
    const labels = data.serieTemporal.map(s => {
      const d = new Date(s.data);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    });
    return {
      labels,
      datasets: [
        {
          label: 'Total',
          data: data.serieTemporal.map(s => s.totalGeral),
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245,158,11,0.1)',
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Meninas',
          data: data.serieTemporal.map(s => s.totalMeninas),
          borderColor: '#ec4899',
          tension: 0.3,
        },
        {
          label: 'Meninos',
          data: data.serieTemporal.map(s => s.totalMeninos),
          borderColor: '#3b82f6',
          tension: 0.3,
        },
      ],
    };
  }, [data]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 } },
    },
  };

  if (loading) {
    return <div className="rc-page"><div className="rc-empty">Carregando dashboard...</div></div>;
  }

  if (!data) return null;

  const { kpis, frequenciaIndividual, ausentes, congDisponiveis } = data;

  return (
    <div className="rc-page">
      <button className="rc-back" onClick={() => navigate('/reuniao-criancas')}>
        <FiArrowLeft /> Voltar
      </button>

      <header className="rc-page-header">
        <div>
          <h1><FiBarChart2 className="rc-icon-title" /> Dashboard - Reuniao de Criancas</h1>
          <p>Analise de presenca e frequencia</p>
        </div>
      </header>

      <div className="rc-filtros">
        <FiFilter />
        <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
          <option value="3m">Ultimos 3 meses</option>
          <option value="6m">Ultimos 6 meses</option>
          <option value="12m">Ultimos 12 meses</option>
          <option value="todos">Todo o periodo</option>
        </select>
        {congDisponiveis?.length > 0 && (
          <select value={congregacao} onChange={(e) => setCongregacao(e.target.value)}>
            <option value="">Todas as congregacoes</option>
            {congDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      <div className="rc-kpis">
        <div className="rc-kpi">
          <FiCalendar />
          <div><span className="rc-kpi-num">{kpis.totalReunioes}</span><span className="rc-kpi-lbl">Reunioes</span></div>
        </div>
        <div className="rc-kpi">
          <FiTrendingUp />
          <div><span className="rc-kpi-num">{kpis.mediaPresentes}</span><span className="rc-kpi-lbl">Media / reuniao</span></div>
        </div>
        <div className="rc-kpi">
          <FiUsers />
          <div><span className="rc-kpi-num">{kpis.totalCriancasCadastradas}</span><span className="rc-kpi-lbl">Criancas cadastradas</span></div>
        </div>
        <div className="rc-kpi">
          <FiUsers />
          <div>
            <span className="rc-kpi-num">{kpis.maisFrequente?.nome?.split(' ')[0] || '-'}</span>
            <span className="rc-kpi-lbl">Mais frequente ({kpis.maisFrequente?.presencas || 0}x)</span>
          </div>
        </div>
      </div>

      {chartData && (
        <section className="rc-section">
          <h2><FiTrendingUp /> Evolucao de presenca</h2>
          <div style={{ height: 300 }}>
            <Line ref={chartRef} data={chartData} options={chartOptions} />
          </div>
        </section>
      )}

      <section className="rc-section">
        <h2><FiUsers /> Frequencia individual</h2>
        <p className="rc-section-sub">
          Percentual de presenca de cada crianca nas reunioes do periodo.
        </p>
        {frequenciaIndividual.length === 0 ? (
          <div className="rc-presentes-vazio">Nenhum dado no periodo selecionado</div>
        ) : (
          <div className="rc-freq-lista">
            {frequenciaIndividual.map((f) => {
              const cor = f.percentual >= 70 ? '#22c55e' : f.percentual >= 50 ? '#eab308' : '#ef4444';
              const idade = calcularIdade(f.dataNascimento);
              return (
                <div key={f.id} className="rc-freq-item">
                  <div className="rc-freq-nome">
                    {f.nome}
                    {idade !== null && <span className="rc-freq-idade">{idade}a</span>}
                  </div>
                  <div className="rc-freq-barra-wrap">
                    <div className="rc-freq-barra" style={{ width: `${f.percentual}%`, background: cor }} />
                  </div>
                  <span className="rc-freq-pct" style={{ color: cor }}>{f.percentual}%</span>
                  <span className="rc-freq-count">{f.presencas}x</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {ausentes.length > 0 && (
        <section className="rc-section rc-section-alerta">
          <h2><FiAlertTriangle style={{ color: '#ef4444' }} /> Criancas ausentes</h2>
          <p className="rc-section-sub">
            Criancas cadastradas que nao compareceram a nenhuma reuniao no periodo.
          </p>
          <div className="rc-ausentes-grid">
            {ausentes.map((a) => {
              const idade = calcularIdade(a.dataNascimento);
              return (
                <div key={a.id} className="rc-ausente-chip">
                  {a.nome} {idade !== null && <span>({idade}a)</span>}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default ReuniaoCriancasDashboard;
